// services/v3/datasource/MockSource.ts — deterministic in-memory backend (SPEC §4.4)
//
// Used by the Playwright suite (`NUXT_PUBLIC_XTABLE_DATA_SOURCE=mock`). It never
// touches Firebase: fixtures shaped like raw RTDB payloads go through the very
// same `normalize*` helpers RtdbSource uses, so the stores see identical data.
//
// Test hooks (all on `window`):
//   __mockOrders         submitted job cards, in order (deduped by jobId)
//   __mockSubmitAttempts number of submitOrder() calls, including failures
//   __mockSubmitJobIds   the jobId of every attempt, including failures
//   __mockDisposeCount   number of dispose() calls
//   __mockFlags          the sticky URL flags currently in effect
//
// URL flags (read from `location.search`, then remembered for the tab so they
// survive the `/v3/qrcode` -> `/v3` redirect and page reloads):
//   mockLock=1      tableState reports `{ lock: true }`
//   mockFailOnce=1  the first submitOrder() fails with a network error
import type {
  DataSource, DataSourceContext, EntityVersions, Unsubscribe, Versioned,
} from '~/types/v3/datasource'
import type {
  ChannelPriceMap, ProductAllowMap, ProductsPayload, SettingsPayload,
  SoldoutMap, StoreProfile, TableDoc, TableItem, TableState,
} from '~/types/v3/entities'
import type { QrToken } from '~/types/v3/token'
import type { QrJobCard, SubmitResult } from '~/types/v3/job'
import {
  normalizeProducts, normalizeSettings, pickChannelPrice, pickMenuSet,
} from './normalize'
import {
  buildMockTokens, MOCK_PAID_DELAY_MS, MOCK_PAID_QRCODE_IDS, MOCK_STORES,
  type MockStoreFixture,
} from './fixtures/mock-store'

// ---------------------------------------------------------------------------
// window test hooks
// ---------------------------------------------------------------------------

export interface MockFlags { lock: boolean; failOnce: boolean }

declare global {
  interface Window {
    __mockOrders?: QrJobCard[]
    __mockSubmitAttempts?: number
    __mockSubmitJobIds?: string[]
    __mockDisposeCount?: number
    __mockFlags?: MockFlags
    /** internal: whether the `mockFailOnce` failure has already been served */
    __mockFailOnceUsed?: boolean
  }
}

const FLAGS_STORAGE_KEY = 'xtable.v3.mockFlags'

const win = (): Window | null => (typeof window === 'undefined' ? null : window)

/** Reads the flags from the URL, falling back to what the tab already saw. */
export function readMockFlags(): MockFlags {
  const w = win()
  if (!w) return { lock: false, failOnce: false }

  let flags: MockFlags = w.__mockFlags ?? { lock: false, failOnce: false }

  if (!w.__mockFlags) {
    try {
      const saved = w.sessionStorage?.getItem(FLAGS_STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<MockFlags>
        flags = { lock: parsed.lock === true, failOnce: parsed.failOnce === true }
      }
    } catch { /* sessionStorage unavailable — ignore */ }
  }

  try {
    const q = new URLSearchParams(w.location.search)
    if (q.get('mockLock') === '1') flags = { ...flags, lock: true }
    if (q.get('mockFailOnce') === '1') flags = { ...flags, failOnce: true }
  } catch { /* no location — ignore */ }

  w.__mockFlags = flags
  try { w.sessionStorage?.setItem(FLAGS_STORAGE_KEY, JSON.stringify(flags)) } catch { /* ignore */ }
  return flags
}

function bumpCounter(key: '__mockSubmitAttempts' | '__mockDisposeCount'): number {
  const w = win()
  if (!w) return 0
  const next = (w[key] ?? 0) + 1
  w[key] = next
  return next
}

function mockOrders(): QrJobCard[] | null {
  const w = win()
  if (!w) return null
  if (!Array.isArray(w.__mockOrders)) w.__mockOrders = []
  return w.__mockOrders
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function clone<T>(value: T): T {
  const sc = (globalThis as { structuredClone?: <V>(v: V) => V }).structuredClone
  if (typeof sc === 'function') {
    try { return sc(value) } catch { /* fall through (Timestamps etc.) */ }
  }
  return JSON.parse(JSON.stringify(value)) as T
}

/** Small, bounded async delay so the UI exercises its loading paths (50-300 ms). */
const DELAY_MIN_MS = 50
const DELAY_MAX_MS = 300
function delay(ms?: number): Promise<void> {
  const wait = ms ?? Math.round(DELAY_MIN_MS + Math.random() * (DELAY_MAX_MS - DELAY_MIN_MS))
  return new Promise((resolve) => { setTimeout(resolve, wait) })
}

const SUBMIT_DELAY_MS = 300

/** Rejected by `submitOrder` when `mockFailOnce=1` — mapped to `network`. */
export class MockNetworkError extends Error {
  readonly code = 'unavailable'
  constructor() {
    super('[mock] simulated network failure')
    this.name = 'MockNetworkError'
  }
}

// ---------------------------------------------------------------------------
// MockSource
// ---------------------------------------------------------------------------

export class MockSource implements DataSource {
  readonly kind = 'mock' as const
  /** the fixtures carry versions, so the store takes the cheap version path */
  readonly supportsVersions = true

  private ctx: DataSourceContext | null = null
  private tokens: Record<string, QrToken> = buildMockTokens()
  private disposed = false
  private timers = new Set<ReturnType<typeof setTimeout>>()

  // -- lifecycle ------------------------------------------------------------

  constructor() {
    // Capture the URL flags as soon as the source exists. `createDataSource()`
    // runs on the entry page (`/v3/qrcode?token=...&mockLock=1`), while
    // `init()` only runs after the SPA has already navigated to `/v3`, where
    // `location.search` no longer carries the flags.
    readMockFlags()
  }

  async init(ctx: DataSourceContext): Promise<void> {
    this.ctx = ctx
    this.disposed = false
    this.tokens = buildMockTokens()
    readMockFlags()
    await delay(60)
  }

  dispose(): void {
    this.disposed = true
    for (const t of this.timers) clearTimeout(t)
    this.timers.clear()
    bumpCounter('__mockDisposeCount')
  }

  /** setTimeout that is cancelled by `dispose()`. */
  private later(fn: () => void, ms: number): void {
    const t = setTimeout(() => {
      this.timers.delete(t)
      if (!this.disposed) fn()
    }, ms)
    this.timers.add(t)
  }

  private store(): MockStoreFixture {
    const id = this.ctx?.storeId ?? 'S1'
    return MOCK_STORES[id] ?? MOCK_STORES.S1
  }

  private settingsPayload(): SettingsPayload {
    return normalizeSettings(clone(this.store().settings))
  }

  // -- versions -------------------------------------------------------------

  async getVersions(): Promise<EntityVersions | null> {
    await delay(50)
    return { ...this.store().versions }
  }

  /** Mock data never changes after init; emit once, then no-op. */
  watchVersions(cb: (v: EntityVersions) => void): Unsubscribe {
    let live = true
    this.later(() => { if (live) cb({ ...this.store().versions }) }, 60)
    return () => { live = false }
  }

  // -- catalog --------------------------------------------------------------

  async fetchSettings(): Promise<Versioned<SettingsPayload>> {
    await delay()
    return { version: this.store().versions.settings, data: this.settingsPayload() }
  }

  async fetchProducts(): Promise<Versioned<ProductsPayload>> {
    await delay()
    return {
      version: this.store().versions.products,
      data: normalizeProducts(clone(this.store().products)),
    }
  }

  async fetchChannelPrice(): Promise<Versioned<ChannelPriceMap>> {
    await delay()
    return {
      version: this.store().versions.channel_price,
      data: pickChannelPrice(this.settingsPayload()),
    }
  }

  async fetchMenuSet(settingId: string): Promise<Versioned<ProductAllowMap>> {
    await delay()
    return {
      version: this.store().versions.menuSet,
      data: pickMenuSet(this.settingsPayload(), settingId),
    }
  }

  async getStoreProfile(): Promise<StoreProfile | null> {
    await delay()
    return { name: this.store().profileName }
  }

  // -- realtime -------------------------------------------------------------

  watchSoldout(cb: (m: SoldoutMap) => void): Unsubscribe {
    let live = true
    this.later(() => { if (live) cb(clone(this.store().soldout)) }, 80)
    return () => { live = false }
  }

  watchTableState(table: string, cb: (s: TableState | null) => void): Unsubscribe {
    let live = true
    this.later(() => { if (live) cb(this.tableStateOf(table)) }, 80)
    return () => { live = false }
  }

  watchTableItems(table: string, cb: (d: TableDoc | null) => void): Unsubscribe {
    let live = true
    this.later(() => { if (live) cb(this.tableItemsOf(table)) }, 90)
    return () => { live = false }
  }

  /**
   * Emits the token, then — for a "paid" qrcode — `null` after 2 s, which the
   * bootstrap store reads as "doc deleted => table paid".
   */
  watchQrcode(qrcodeId: string, cb: (t: QrToken | null) => void): Unsubscribe {
    let live = true
    const token = this.tokens[qrcodeId] ?? null
    this.later(() => { if (live) cb(token ? clone(token) : null) }, 80)
    if (token && MOCK_PAID_QRCODE_IDS.includes(qrcodeId)) {
      this.later(() => { if (live) cb(null) }, MOCK_PAID_DELAY_MS)
    }
    return () => { live = false }
  }

  // -- point reads ----------------------------------------------------------

  async getQrcode(qrcodeId: string): Promise<QrToken | null> {
    await delay()
    const token = this.tokens[qrcodeId]
    return token ? clone(token) : null
  }

  async getTableState(table: string): Promise<TableState | null> {
    await delay(50)
    return this.tableStateOf(table)
  }

  async getServerNowISO(): Promise<string> {
    await delay(50)
    return new Date().toISOString()
  }

  private tableStateOf(table: string): TableState | null {
    const raw = this.store().tableState[table]
    if (!raw) return null
    const state = clone(raw) as TableState
    if (readMockFlags().lock) state.lock = true
    return state
  }

  private tableItemsOf(table: string): TableDoc | null {
    const raw = this.store().tableItems[table]
    if (!raw) return null
    return { items: clone(raw.items) as unknown as TableItem[] }
  }

  // -- submit ---------------------------------------------------------------

  /**
   * Resolves after 300 ms. The job is appended to `window.__mockOrders`; a
   * second submit of the same jobId reports `alreadyExisted` and pushes nothing
   * (this is what makes the retry path idempotent in `multi-qr.spec.ts`).
   */
  async submitOrder(job: QrJobCard): Promise<SubmitResult> {
    bumpCounter('__mockSubmitAttempts')
    const w0 = win()
    if (w0) {
      if (!Array.isArray(w0.__mockSubmitJobIds)) w0.__mockSubmitJobIds = []
      w0.__mockSubmitJobIds.push(job.jobId)
    }
    await delay(SUBMIT_DELAY_MS)

    const flags = readMockFlags()
    const w = win()
    if (flags.failOnce && w && !w.__mockFailOnceUsed) {
      w.__mockFailOnceUsed = true
      return { ok: false, jobId: job.jobId, reason: 'network', error: new MockNetworkError() }
    }

    const state = this.tableStateOf(job.table)
    if (state?.lock === true) return { ok: false, jobId: job.jobId, reason: 'locked' }

    const orders = mockOrders()
    if (!orders) return { ok: true, jobId: job.jobId, simulated: true, alreadyExisted: false }

    if (orders.some((o) => o.jobId === job.jobId)) {
      return { ok: true, jobId: job.jobId, simulated: true, alreadyExisted: true }
    }
    // keep the live Timestamp on `expireAt`; only the rest is snapshotted
    orders.push({ ...job, items: clone(job.items) })
    return { ok: true, jobId: job.jobId, simulated: true, alreadyExisted: false }
  }
}
